import { useMemo, useState } from 'preact/hooks'
import { currentMemberId } from './auth/household'
import { BottomNav, type Tab } from './components/BottomNav'
import { MemberChips } from './components/MemberChips'
import { SyncBadge } from './components/SyncBadge'
import { DEFAULT_PLAN } from './data/default-plan'
import { dateKey } from './lib/cycle'
import { memberName, visibleMembers } from './lib/members'
import { notifyTaskAssigned } from './lib/nudge'
import { buildPlan, dayProgress, tasksForDate, tasksForMember } from './lib/plan'
import { Month } from './pages/Month'
import { Settings } from './pages/Settings'
import { Today } from './pages/Today'
import { doneIds, live, useAppStore } from './store/store'
import type { NewChore } from './types'

/**
 * The shell: which tab is open, which day is selected, **whose list is being
 * looked at**, and the header that all three feed. The three pages own their
 * own markup.
 *
 * The plan is passed down rather than imported by the pages, so this component
 * is the whole seam between "the plan is code" and "the plan is data" —
 * `docs/architecture-review.md` §1.3. Both halves now run through it: the
 * built-in rotation is still the literal, and the household's own chores come
 * out of the store and are folded in by `buildPlan`. No page below knows the
 * difference, which is what that seam was for.
 *
 * The member is the same shape of seam one layer up. `tasksForMember` filters
 * the day's plan before any page sees it, so the day list, the header orb and
 * the month grid cannot disagree about what today contains — and a page still
 * cannot tell whether a chore belongs to one person or to the house.
 */
export default function App() {
  const [tab, setTab] = useState<Tab>('today')
  const [selectedDate, setSelectedDate] = useState(() => new Date())

  const completions = useAppStore((s) => s.completions)
  const customTasks = useAppStore((s) => s.tasks)
  const storedMembers = useAppStore((s) => s.members)
  const toggleCompletion = useAppStore((s) => s.toggleCompletion)
  const addTask = useAppStore((s) => s.addTask)

  // Resolved by AuthGate before this component ever renders, and constant for
  // the life of the session. It is who you *are*, as opposed to whose list you
  // are looking at, and only the first of those is fixed.
  const selfMemberId = currentMemberId()
  const done = useMemo(() => doneIds(completions), [completions])

  const members = useMemo(
    () => visibleMembers(live(storedMembers), selfMemberId),
    [storedMembers, selfMemberId],
  )

  const [pickedMemberId, setPickedMemberId] = useState(selfMemberId)
  // A member can be removed on the other phone while their list is open here.
  // Falling back to your own list beats rendering somebody who is no longer in
  // the household, and beats an effect that would fight the user's next tap.
  const memberId = members.some((member) => member.id === pickedMemberId)
    ? pickedMemberId
    : selfMemberId

  // Memoised on the rows rather than rebuilt every render: the month grid calls
  // `tasksForDate` once per day of the month, and a new plan object on every
  // render would rebuild all thirty-one lists for a keystroke elsewhere.
  const plan = useMemo(() => buildPlan(DEFAULT_PLAN, live(customTasks)), [customTasks])
  const taskDate = dateKey(selectedDate)
  const tasks = useMemo(
    () => tasksForMember(tasksForDate(selectedDate, plan), memberId),
    [taskDate, plan, memberId],
  )
  const progress = dayProgress(tasks, taskDate, memberId, done)

  // No await, no network. The store marks the row dirty and the sync engine
  // picks it up ~2 s later; a failed request then retries instead of dropping
  // the tick, which is what the draft's `pushCompletion` from here did.
  function toggleTask(taskId: string) {
    toggleCompletion(taskDate, taskId, memberId)
  }

  /**
   * Add a chore, then — only if it landed on somebody else's list — tell them.
   *
   * In that order, and never awaited before the store write: the chore exists
   * locally the moment the form is submitted, and the notification is a side
   * effect that a tunnel is allowed to cost. Resolves to a line worth showing,
   * or null when there is nothing to say.
   */
  async function addChore(chore: NewChore): Promise<string | null> {
    const taskId = addTask(chore)
    if (!taskId) return null
    if (!chore.assigneeId || chore.assigneeId === selfMemberId) return null

    const failure = await notifyTaskAssigned(taskId)
    if (!failure) return null
    return `Tâche ajoutée pour ${memberName(members, chore.assigneeId)}, mais la notification n’est pas partie.`
  }

  function moveDay(delta: number) {
    const next = new Date(selectedDate)
    next.setDate(next.getDate() + delta)
    setSelectedDate(next)
  }

  return (
    <main class="app-shell">
      <header class="hero">
        <div>
          <p class="eyebrow">MAISON ZEN</p>
          <h1>La maison tourne, pas toi 😄</h1>
          <p>Ton planning quotidien, tranquille et cochable.</p>
          <SyncBadge />
        </div>
        <div class="progress-orb">
          <strong>{progress.percent}%</strong>
          <span>fait</span>
        </div>
      </header>

      {tab !== 'settings' && (
        <MemberChips members={members} selected={memberId} onSelect={setPickedMemberId} />
      )}

      {tab === 'today' && (
        <Today
          date={selectedDate}
          taskDate={taskDate}
          tasks={tasks}
          progress={progress}
          memberId={memberId}
          members={members}
          done={done}
          onMoveDay={moveDay}
          onToggle={toggleTask}
          onAddTask={addChore}
        />
      )}

      {tab === 'month' && (
        <Month
          anchor={selectedDate}
          plan={plan}
          done={done}
          memberId={memberId}
          onSelectDate={(date) => {
            setSelectedDate(date)
            setTab('today')
          }}
        />
      )}

      {tab === 'settings' && <Settings />}

      <BottomNav tab={tab} onSelect={setTab} />
    </main>
  )
}
